import React from 'react'
import { navigate } from '@reach/router'
import { Container as BlockContainer, Header } from '../Blocks'
import {
    Container,
    Form,
    FormGroup,
    Label,
    Row,
    Col,
    Button,
    Input
} from 'reactstrap'

import NumberFormat from 'react-number-format'
import NumberInput from './Fields/NumberInput'
import { PMT } from 'formulajs'

const loanTypes = {

    "Emergency Loan": {
        name: "Multipurpose Loan",
        rate: 24,
        maximumLoanableAmount: 30000,
        maximumMonthTerms: 18
    },

    "Cash Loan": {
        name: "Cash Loan",
        rate: 15,
        maximumLoanableAmount: 5000000,
        maximumMonthTerms: 60
    },

    "Appliance Loan": {
        name: "Appliance Loan",
        rate: 14,
        maximumLoanableAmount: 30000,
        maximumMonthTerms: 36,
        equity: 15
    },

    "Memorial Lot Loan": {
        name: "Memorial Lot Loan",
        rate: 12,
        maximumLoanableAmount: null,
        maximumMonthTerms: 60
    },

    "Housing Loan": {
        name: "Housing Loan",
        rate: 12,
        maximumLoanableAmount: 5000000,
        maximumMonthTerms: 240
    },

    "Micro and Small Enterprise Loan": {
        name: "Micro and Small Enterprise Loan",
        rate: 24,
        maximumLoanableAmount: 300000,
        maximumMonthTerms: 36
    },

    "Business Loan": {
        name: "Business Loan",
        rate: 11,
        maximumLoanableAmount: 15000000,
        maximumMonthTerms: 84
    },

    "Expanded Credit Line (ECL-FEEDS)": {
        name: "Expanded Credit Line (ECL-FEEDS)",
        rate: 15,
        maximumLoanableAmount: 5000000,
        maximumMonthTerms: 7
    },

    "Vehicle Loan - Car": {
        name: "Vehicle Loan - Car",
        rate: 13,
        maximumLoanableAmount: 5000000,
        maximumMonthTerms: 60,
        equity: 20
    },

    "Vehicle Loan - Motorcycle & Tricycle": {
        name: "Vehicle Loan - Motorcycle & Tricycle",
        rate: 14,
        maximumLoanableAmount: 120000,
        maximumMonthTerms: 36,
        equity: 20
    },

    "Agrifarm Equipment Loan": {
        name: "Agrifarm Equipment Loan",
        rate: 14,
        maximumLoanableAmount: 1500000,
        maximumMonthTerms: 36
    },

    "Solar Loan": {
        name: "Solar Loan",
        rate: 15.96,
        maximumLoanableAmount: 5000000,
        maximumMonthTerms: 60,
        equity: 10
    }
}

const loanCurrencies = [
    {
        name: 'PHILIPPINE PESO',
        code: 'PHP'
    }
]

class LoanCalculator extends React.Component {

    constructor(props) {
        super(props)

        this.state = {

            loanType: "Emergency Loan",
            loanCurrency: 'PHP',
            loanStartDate: '',

            totalPurchaseAmount: 0,
            equity: 0,
            loanAmount: 0,

            loanTermMonth: 0,
            loanInterestMonthly: 0,

            monthlyPayment: 0,
            totalInterestPayment: 0,
            totalAmountRepayable: 0
        }
    }

    handleChange = (e) => {

        this.setState(
            {
                [e.target.name]: e.target.value
            },
            () => this.calculate()
        )
    }

    handleValueChange = (name, value) => {

        this.setState(
            {
                [name]: value.floatValue || 0
            },
            () => this.calculate()
        )
    }

    calculate = () => {

        this.setState((state) => {

            const selectedLoan = loanTypes[state.loanType]

            const totalPurchaseAmount =
                Math.max(Number(state.totalPurchaseAmount) || 0, 0)

            const loanTermMonth =
                Math.max(Number(state.loanTermMonth) || 0, 0)

            const equityPercent = selectedLoan.equity || 0

            const specialLoanTypes = [
                "Vehicle Loan - Car",
                "Vehicle Loan - Motorcycle & Tricycle",
                "Appliance Loan",
                "Solar Loan"
            ]

            const isSpecialLoan =
                specialLoanTypes.includes(state.loanType)

            let equityAmount = 0
            let loanAmount = state.loanAmount

            // AUTO COMPUTE FOR SPECIAL LOANS
            if (isSpecialLoan) {

                equityAmount =
                    totalPurchaseAmount * (equityPercent / 100)

                loanAmount =
                    totalPurchaseAmount - equityAmount

                // Prevent negative
                if (loanAmount < 0) {
                    loanAmount = 0
                }

                // Respect maximum loanable amount
                if (
                    selectedLoan.maximumLoanableAmount &&
                    loanAmount > selectedLoan.maximumLoanableAmount
                ) {
                    loanAmount =
                        selectedLoan.maximumLoanableAmount
                }
            }

            // MONTHLY INTEREST
            let monthlyInterest = 0

            // SOLAR LOAN = FIXED 1.33%
            if (state.loanType === "Solar Loan") {
                monthlyInterest = 1.33
            }
            else {
                monthlyInterest = selectedLoan.rate / 12
            }

            // PAYMENT COMPUTATION
            let monthlyPayment = 0
            let totalAmountPaid = 0
            let totalInterestPayment = 0

            if (
                loanAmount > 0 &&
                loanTermMonth > 0
            ) {

                monthlyPayment =
                    PMT(
                        monthlyInterest / 100,
                        loanTermMonth,
                        loanAmount,
                        0,
                        0
                    ) * -1

                totalAmountPaid =
                    monthlyPayment * loanTermMonth

                totalInterestPayment =
                    totalAmountPaid - loanAmount
            }

            return {

                equity: equityAmount,
                loanAmount: loanAmount,

                loanInterestMonthly: monthlyInterest,

                monthlyPayment: monthlyPayment,

                totalAmountRepayable: totalAmountPaid,

                totalInterestPayment: totalInterestPayment
            }
        })
    }

    apply = (e) => {
        e.preventDefault()
        navigate('/inquire')
    }

    render() {

        const specialLoanTypes = [
            "Vehicle Loan - Car",
            "Vehicle Loan - Motorcycle & Tricycle",
            "Appliance Loan",
            "Solar Loan"
        ]

        const isSpecialLoan =
            specialLoanTypes.includes(this.state.loanType)

        return (

            <BlockContainer>

                <Container>

                    <Row>
                        <Col md="12">

                            <Header
                                headerNormal
                                title="Koopinoy Loan Calculator"
                                underlined
                            >
                                Use the loan repayment calculator below
                                to get an estimate of your monthly
                                installments, total interest cost and
                                total loan cost.
                            </Header>

                        </Col>
                    </Row>

                    <Form onSubmit={this.apply}>

                        <Row>

                            {/* LEFT SIDE */}
                            <Col lg="7" sm="12">

                                <div className="bg-info p-3">

                                    {/* LOAN TYPE */}
                                    <Row form>

                                        <Col md="6">

                                            <FormGroup>

                                                <Label for="loanType">
                                                    Loan Type
                                                </Label>

                                                <Input
                                                    value={this.state.loanType}
                                                    onChange={this.handleChange}
                                                    type="select"
                                                    name="loanType"
                                                    id="loanType"
                                                >

                                                    {
                                                        Object.keys(loanTypes).map((loanKey, key) => {

                                                            let loanType =
                                                                loanTypes[loanKey]

                                                            return (
                                                                <option
                                                                    key={`${key}-${loanKey}`}
                                                                    value={loanKey}
                                                                >
                                                                    {loanType.name}
                                                                </option>
                                                            )
                                                        })
                                                    }

                                                </Input>

                                            </FormGroup>

                                        </Col>

                                        <Col md="6">

                                            <FormGroup>

                                                <Label for="loanCurrency">
                                                    Currency
                                                </Label>

                                                <Input
                                                    value={this.state.loanCurrency}
                                                    onChange={this.handleChange}
                                                    type="select"
                                                    name="loanCurrency"
                                                    id="loanCurrency"
                                                >

                                                    {
                                                        loanCurrencies.map((currency) => {

                                                            return (
                                                                <option
                                                                    key={currency.code}
                                                                    value={currency.code}
                                                                >
                                                                    {currency.code} - {currency.name}
                                                                </option>
                                                            )
                                                        })
                                                    }

                                                </Input>

                                            </FormGroup>

                                        </Col>

                                    </Row>

                                    {/* SPECIAL LOAN UI */}
                                    {
                                        isSpecialLoan && (

                                            <>
                                                <Row form>

                                                    <Col md="6">

                                                        <FormGroup>

                                                            <Label>
                                                                Total Purchase Price
                                                            </Label>

                                                            <NumberInput
                                                                loantype={this.state.loanType}
                                                                name="totalPurchaseAmount"
                                                                id="totalPurchaseAmount"
                                                                min={0}
                                                                interval={100}
                                                                decimalScale={2}
                                                                thousandSeparator={true}
                                                                className="form-control text-center"
                                                                onValueChange={(val) => {
                                                                    this.handleValueChange(
                                                                        'totalPurchaseAmount',
                                                                        val
                                                                    )
                                                                }}
                                                                required
                                                            />

                                                        </FormGroup>

                                                    </Col>

                                                    <Col md="6">

                                                        <FormGroup>

                                                            <Label>
                                                                Equity ({loanTypes[this.state.loanType].equity}%)
                                                            </Label>

                                                            <div className="form-control text-center bg-light">

                                                                <NumberFormat
                                                                    value={this.state.equity}
                                                                    displayType="text"
                                                                    thousandSeparator={true}
                                                                    decimalScale={2}
                                                                    fixedDecimalScale={true}
                                                                    prefix="PHP "
                                                                />

                                                            </div>

                                                        </FormGroup>

                                                    </Col>

                                                </Row>

                                                <Row form>

                                                    <Col md="6">

                                                        <FormGroup>

                                                            <Label>
                                                                Loanable Amount
                                                            </Label>

                                                            <div className="form-control text-center bg-light">

                                                                <NumberFormat
                                                                    value={this.state.loanAmount}
                                                                    displayType="text"
                                                                    thousandSeparator={true}
                                                                    decimalScale={2}
                                                                    fixedDecimalScale={true}
                                                                    prefix="PHP "
                                                                />

                                                            </div>

                                                        </FormGroup>

                                                    </Col>

                                                    <Col md="6">

                                                        <FormGroup>

                                                            <Label>
                                                                Loan Term (Months)
                                                            </Label>

                                                            <NumberInput
                                                                loantype={this.state.loanType}
                                                                name="loanTermMonth"
                                                                id="loanTermMonth"
                                                                min={1}
                                                                max={
                                                                    loanTypes[this.state.loanType]
                                                                        .maximumMonthTerms
                                                                }
                                                                interval={1}
                                                                className="form-control text-center"
                                                                onValueChange={(val) => {
                                                                    this.handleValueChange(
                                                                        'loanTermMonth',
                                                                        val
                                                                    )
                                                                }}
                                                                required
                                                            />

                                                        </FormGroup>

                                                    </Col>

                                                </Row>
                                            </>
                                        )
                                    }

                                    {/* NORMAL LOAN UI */}
                                    {
                                        !isSpecialLoan && (

                                            <Row form>

                                                <Col md="6">

                                                    <FormGroup>

                                                        <Label>
                                                            Loan Amount
                                                        </Label>

                                                        <NumberInput
                                                            loantype={this.state.loanType}
                                                            name="loanAmount"
                                                            id="loanAmount"
                                                            min={0}
                                                            max={
                                                                loanTypes[this.state.loanType]
                                                                    .maximumLoanableAmount
                                                            }
                                                            interval={100}
                                                            decimalScale={2}
                                                            thousandSeparator={true}
                                                            className="form-control text-center"
                                                            onValueChange={(val) => {
                                                                this.handleValueChange(
                                                                    'loanAmount',
                                                                    val
                                                                )
                                                            }}
                                                            required
                                                        />

                                                    </FormGroup>

                                                </Col>

                                                <Col md="6">

                                                    <FormGroup>

                                                        <Label>
                                                            Loan Term (Months)
                                                        </Label>

                                                        <NumberInput
                                                            loantype={this.state.loanType}
                                                            name="loanTermMonth"
                                                            id="loanTermMonth"
                                                            min={1}
                                                            max={
                                                                loanTypes[this.state.loanType]
                                                                    .maximumMonthTerms
                                                            }
                                                            interval={1}
                                                            className="form-control text-center"
                                                            onValueChange={(val) => {
                                                                this.handleValueChange(
                                                                    'loanTermMonth',
                                                                    val
                                                                )
                                                            }}
                                                            required
                                                        />

                                                    </FormGroup>

                                                </Col>

                                            </Row>
                                        )
                                    }

                                    {/* DATE + INTEREST */}
                                    <Row form>

                                        <Col md="6">

                                            <FormGroup>

                                                <Label>
                                                    Loan Start Date
                                                </Label>

                                                <Input
                                                    value={this.state.loanStartDate}
                                                    onChange={this.handleChange}
                                                    type="date"
                                                    id="loanStartDate"
                                                    name="loanStartDate"
                                                    required
                                                />

                                            </FormGroup>

                                        </Col>

                                        <Col md="6">

                                            <FormGroup>

                                                <Label>
                                                    Interest (Monthly)
                                                </Label>

                                                <div className="form-control text-center bg-light">

                                                    <NumberFormat
                                                        value={this.state.loanInterestMonthly}
                                                        displayType="text"
                                                        suffix="%"
                                                        decimalScale={2}
                                                        fixedDecimalScale={true}
                                                    />

                                                </div>

                                            </FormGroup>

                                        </Col>

                                    </Row>

                                </div>

                            </Col>

                            {/* RIGHT SIDE */}
                            <Col lg="5" sm="12">

                                <Container className="text-center py-3">

                                    <div className="pb-3">

                                        <p className="lead text-primary">
                                            MONTHLY PAYMENT
                                        </p>

                                        <p className="lead text-primary">

                                            <b>

                                                <NumberFormat
                                                    value={this.state.monthlyPayment}
                                                    displayType="text"
                                                    prefix="PHP "
                                                    decimalScale={2}
                                                    fixedDecimalScale={true}
                                                    thousandSeparator={true}
                                                />

                                            </b>

                                        </p>

                                    </div>

                                    <div className="pb-2">

                                        <Row>

                                            <Col sm="6">

                                                <p>
                                                    TOTAL INTEREST PAYMENT
                                                </p>

                                                <p className="lead">

                                                    <b>

                                                        <NumberFormat
                                                            value={this.state.totalInterestPayment}
                                                            displayType="text"
                                                            prefix="PHP "
                                                            decimalScale={2}
                                                            fixedDecimalScale={true}
                                                            thousandSeparator={true}
                                                        />

                                                    </b>

                                                </p>

                                            </Col>

                                            <Col sm="6">

                                                <p>
                                                    TOTAL AMOUNT REPAYABLE
                                                </p>

                                                <p className="lead">

                                                    <b>

                                                        <NumberFormat
                                                            value={this.state.totalAmountRepayable}
                                                            displayType="text"
                                                            prefix="PHP "
                                                            decimalScale={2}
                                                            fixedDecimalScale={true}
                                                            thousandSeparator={true}
                                                        />

                                                    </b>

                                                </p>

                                            </Col>

                                        </Row>

                                    </div>

                                    <Button
                                        size="lg"
                                        color="primary"
                                    >
                                        Apply Now
                                    </Button>

                                </Container>

                            </Col>

                        </Row>

                    </Form>

                    <div className="text-left ml-3 mt-4">

                        <p>
                            **Patronage Refund is not included in
                            KooPinoy Calculator computation.
                        </p>

                        <p>
                            Disclaimer: The loan calculator provides
                            estimated amounts only. Final loan terms
                            and total payable may vary based on
                            evaluation and applicable charges at the
                            time of application.
                        </p>

                    </div>

                </Container>

            </BlockContainer>
        )
    }
}

export default LoanCalculator